import {
  ADMIN_NAMESPACES,
  DEFAULT_APP_LOCALE,
  getDirection,
  isAppLocale,
  loadAdminResources,
  LOCALE_STORAGE_KEY,
  LOCALES,
  TR_ADMIN_RESOURCES,
  type AppLocale,
} from '@duvamarine/i18n/admin';
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

export function applyDocumentLocale(locale: string): void {
  document.documentElement.lang = locale;
  document.documentElement.dir = getDirection(locale);
}

export function currentLocale(): AppLocale {
  const locale = i18n.resolvedLanguage;
  return isAppLocale(locale) ? locale : DEFAULT_APP_LOCALE;
}

/** Staff work in Turkish by default; the browser language is not used for the admin console. */
function storedLocale(): AppLocale {
  try {
    const value = window.localStorage.getItem(LOCALE_STORAGE_KEY);
    return isAppLocale(value) ? value : DEFAULT_APP_LOCALE;
  } catch {
    return DEFAULT_APP_LOCALE;
  }
}

// Turkish is bundled; the other languages are fetched on first use (see changeLocale).
void i18n.use(initReactI18next).init({
  resources: { [DEFAULT_APP_LOCALE]: TR_ADMIN_RESOURCES },
  partialBundledLanguages: true,
  lng: DEFAULT_APP_LOCALE,
  ns: [...ADMIN_NAMESPACES],
  defaultNS: 'common',
  fallbackLng: DEFAULT_APP_LOCALE,
  supportedLngs: [...LOCALES],
  load: 'languageOnly',
  interpolation: { escapeValue: false },
});

i18n.on('languageChanged', (locale) => {
  applyDocumentLocale(locale);
  try {
    if (isAppLocale(locale)) window.localStorage.setItem(LOCALE_STORAGE_KEY, locale);
  } catch {
    // Private mode: the choice lasts for this visit only.
  }
});
applyDocumentLocale(currentLocale());

/** Loads a language's texts on first use and switches to it. */
export async function changeLocale(locale: AppLocale): Promise<void> {
  if (!i18n.hasResourceBundle(locale, 'admin')) {
    const bundle = await loadAdminResources(locale);
    for (const namespace of ADMIN_NAMESPACES) {
      i18n.addResourceBundle(locale, namespace, bundle[namespace], true, true);
    }
  }
  if (i18n.resolvedLanguage !== locale) await i18n.changeLanguage(locale);
}

/** Settles once the stored language is ready; main.tsx renders after it. */
export const i18nReady: Promise<void> = changeLocale(storedLocale()).catch(() => undefined);

export default i18n;
